import { cva } from 'class-variance-authority';

/**
 * Class constants for the notification surface.
 *
 * Every colour is a token from `src/styles/globals.css`. A raw palette class
 * here would drift from the theme the moment a token moves, and dark mode could
 * not correct it — the same reason `ml/no-foreign-colour-utility` exists.
 *
 * The tone tints the WHOLE CARD with the status's soft wash, and colours the
 * icon and the left rail to match. The wash is the same family as the rail, so
 * the card reads as one object rather than a white box with a coloured stripe.
 * The title stays `text-content` in every tone so the words are always legible
 * — the tint carries the tone, the text carries the meaning.
 */
export const notificationStyles = {
  /**
   * The fixed region the notifications stack into.
   *
   * Top-right, below the top bar, above every other layer (`z-[60]` sits over
   * the dialog's `z-50`). `pointer-events-none` on the region and
   * `pointer-events-auto` on each card means the empty region never swallows a
   * click meant for the page behind it.
   */
  viewport:
    'pointer-events-none fixed right-4 top-4 z-[60] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2',

  /** One notification card. */
  root: cva(
    [
      'pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden',
      'rounded-surface border border-border p-3 pl-4 shadow-lg',
      'transition-all duration-200',
      'data-[starting-style]:translate-x-2 data-[starting-style]:opacity-0',
      'data-[ending-style]:translate-x-2 data-[ending-style]:opacity-0',
    ],
    {
      variants: {
        tone: {
          success: 'border-l-4 border-l-positive bg-positive-soft',
          error: 'border-l-4 border-l-critical bg-critical-soft',
          warning: 'border-l-4 border-l-caution bg-caution-soft',
          info: 'border-l-4 border-l-accent bg-accent-soft',
        },
      },
      defaultVariants: { tone: 'info' },
    },
  ),

  /** The tone icon. Decorative — the title is the accessible content. */
  icon: cva('mt-0.5 size-4 shrink-0', {
    variants: {
      tone: {
        success: 'text-positive',
        error: 'text-critical',
        warning: 'text-caution',
        info: 'text-accent',
      },
    },
    defaultVariants: { tone: 'info' },
  }),

  /** The text column: title, description, action. */
  body: 'flex min-w-0 flex-1 flex-col gap-1',

  title: 'text-sm font-medium text-content',

  description: 'text-xs text-content-muted',

  /** The optional action button — a text button, not a filled one. */
  action:
    'mt-1 w-fit rounded-control border border-border-strong bg-transparent px-2 py-1 text-xs font-medium text-content transition-colors hover:bg-surface-raised',

  /** The dismiss control. A real button with an accessible name. */
  close:
    'shrink-0 rounded-control p-1 text-content-subtle transition-colors hover:bg-surface-sunken hover:text-content',
} as const;
